/**
 * Add Question to Quiz Screen
 * Supports selecting a section for the question
 */

import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { QuizCategory, QuestionType, QuestionDifficulty, QuizOption } from "@/modules/quiz";
import * as ImagePicker from "expo-image-picker";
import * as FileSystem from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";

/**
 * Section interface
 */
interface QuizSection {
  id: string;
  name: string;
  order_index: number;
}

const QUESTION_TYPES: { value: QuestionType; label: string; icon: string }[] = [
  { value: "multiple_choice", label: "Meerkeuze", icon: "list" },
  { value: "true_false", label: "Waar/Onwaar", icon: "swap-horizontal" },
];

const DIFFICULTY_OPTIONS: { value: QuestionDifficulty; label: string; color: string }[] = [
  { value: "easy", label: "Makkelijk", color: "#10B981" },
  { value: "medium", label: "Gemiddeld", color: "#F59E0B" },
  { value: "hard", label: "Moeilijk", color: "#EF4444" },
];

export default function AddQuestionScreen() {
  const router = useRouter();
  const { id, sectionId: initialSectionId } = useLocalSearchParams<{ id: string; sectionId?: string }>();
  const colors = useThemeColors();
  const { isDark } = useTheme();
  const { activeProfile } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<QuizCategory[]>([]);
  const [sections, setSections] = useState<QuizSection[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Form
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState<QuestionType>("multiple_choice");
  const [sectionId, setSectionId] = useState<string | null>(initialSectionId || null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>("medium");
  const [explanation, setExplanation] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [options, setOptions] = useState<QuizOption[]>([
    { text: "", is_correct: true },
    { text: "", is_correct: false },
    { text: "", is_correct: false },
    { text: "", is_correct: false },
  ]);

  /**
   * Fetch categories and sections
   */
  useEffect(() => {
    const fetchData = async () => {
      // Fetch categories
      const { data: catData } = await supabase
        .from("quiz_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (catData) setCategories(catData as QuizCategory[]);

      // Fetch sections for this quiz
      const { data: secData } = await supabase
        .from("quiz_sections")
        .select("id, name, order_index")
        .eq("quiz_id", id)
        .order("order_index");
      if (secData) setSections(secData);
    };
    fetchData();
  }, [id]);

  /**
   * Handle option text change
   */
  const handleOptionChange = (index: number, text: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, text } : o)));
  };

  /**
   * Handle correct answer selection
   */
  const handleCorrectChange = (index: number) => {
    setOptions((prev) => prev.map((o, i) => ({ ...o, is_correct: i === index })));
  };

  /**
   * Add an option
   */
  const handleAddOption = () => {
    if (options.length < 6) {
      setOptions((prev) => [...prev, { text: "", is_correct: false }]);
    }
  };

  /**
   * Remove an option
   */
  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      const wasCorrect = options[index].is_correct;
      setOptions((prev) => {
        const newOpts = prev.filter((_, i) => i !== index);
        if (wasCorrect && newOpts.length > 0) newOpts[0].is_correct = true;
        return newOpts;
      });
    }
  };

  /**
   * Set true/false options
   */
  const setTrueFalse = (correctIsTrue: boolean) => {
    setOptions([
      { text: "Waar", is_correct: correctIsTrue },
      { text: "Onwaar", is_correct: !correctIsTrue },
    ]);
  };

  /**
   * Handle question type change
   */
  useEffect(() => {
    if (questionType === "true_false") {
      setTrueFalse(true);
    } else if (questionType === "multiple_choice" && options.length < 2) {
      setOptions([
        { text: "", is_correct: true },
        { text: "", is_correct: false },
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ]);
    }
  }, [questionType]);

  /**
   * Upload image to storage
   */
  const uploadImage = async (uri: string) => {
    setIsUploadingImage(true);
    
    try {
      const base64 = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      const fileName = `quiz-question-${Date.now()}.jpg`;
      const filePath = `quiz-images/${id}/${fileName}`;

      // Using quiz-assets bucket for quiz question images
      const { error: uploadError } = await supabase.storage
        .from("quiz-assets")
        .upload(filePath, decode(base64), {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("quiz-assets")
        .getPublicUrl(filePath);

      setImageUrl(publicUrl);
    } catch (uploadErr) {
      console.error("Error during upload:", uploadErr);
      Alert.alert("Fout", "Kon afbeelding niet uploaden. Probeer het opnieuw.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  /**
   * Pick and upload image
   * Matches the working implementation from chat module
   */
  const handlePickImage = async () => {
    try {
      // Request permissions first (like chat module does)
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Geen toegang", "Geef toegang tot je foto's om een afbeelding toe te voegen");
        return;
      }

      // Use the new API format (not deprecated MediaTypeOptions)
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"], // New API format instead of MediaTypeOptions.Images
        allowsEditing: false, // Disabled to prevent navigation issues
        quality: 0.8,
      });

      if (result.canceled) return;

      if (result.assets && result.assets[0]) {
        await uploadImage(result.assets[0].uri);
      }
    } catch (error) {
      if (error instanceof Error && !error.message.includes("cancel")) {
        console.error("Error picking image:", error);
        Alert.alert("Fout", "Kon afbeelding niet selecteren");
      }
    }
  };

  /**
   * Remove image
   */
  const handleRemoveImage = () => {
    setImageUrl(null);
  };

  /**
   * Validate form
   */
  const validate = (): boolean => {
    if (!question.trim()) {
      Alert.alert("Fout", "Vul een vraag in");
      return false;
    }
    
    const filled = options.filter((o) => o.text.trim());
    if (filled.length < 2) {
      Alert.alert("Fout", "Vul minimaal 2 opties in");
      return false;
    }
    if (!options.some((o) => o.is_correct && o.text.trim())) {
      Alert.alert("Fout", "Selecteer een correct antwoord");
      return false;
    }
    
    return true;
  };

  /**
   * Save question
   */
  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      const finalOptions = options.filter((o) => o.text.trim());

      // Get the highest order_index for questions in this section/quiz
      const { data: existingQuestions } = await supabase
        .from("quiz_questions")
        .select("order_index")
        .eq("quiz_id", id)
        .order("order_index", { ascending: false })
        .limit(1);

      const nextOrderIndex = existingQuestions && existingQuestions.length > 0 
        ? (existingQuestions[0].order_index || 0) + 1 
        : 0;

      const { error } = await supabase.from("quiz_questions").insert({
        quiz_id: id,
        section_id: sectionId,
        question: question.trim(),
        question_type: questionType,
        category_id: categoryId,
        difficulty,
        explanation: explanation.trim() || null,
        image_url: imageUrl,
        options: finalOptions,
        order_index: nextOrderIndex,
        is_family_specific: true,
        created_by: activeProfile?.id,
        is_active: true,
      });

      if (error) throw error;

      router.back();
    } catch (error) {
      console.error(error);
      Alert.alert("Fout", "Kon vraag niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Nieuwe Vraag
            </Text>
          </View>
          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl"
            style={{ backgroundColor: isSaving ? colors.border : colors.primary }}
            accessibilityRole="button"
            accessibilityLabel="Vraag opslaan"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text className="font-bold text-white">Opslaan</Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" keyboardShouldPersistTaps="handled">
          {/* Section Selection */}
          {sections.length > 0 && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Sectie
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <TouchableOpacity
                  onPress={() => setSectionId(null)}
                  className="px-4 py-2 rounded-full mr-2"
                  style={{
                    backgroundColor: sectionId === null ? colors.primary : colors.surface,
                    borderWidth: sectionId === null ? 0 : 1,
                    borderColor: colors.border,
                  }}
                >
                  <Text style={{ color: sectionId === null ? "#FFF" : colors.text }}>
                    Geen sectie
                  </Text>
                </TouchableOpacity>
                {sections.map((s, index) => (
                  <TouchableOpacity
                    key={s.id}
                    onPress={() => setSectionId(s.id)}
                    className="px-4 py-2 rounded-full mr-2 flex-row items-center"
                    style={{
                      backgroundColor: sectionId === s.id ? colors.primary : colors.surface,
                      borderWidth: sectionId === s.id ? 0 : 1,
                      borderColor: colors.border,
                    }}
                  >
                    <View
                      className="w-5 h-5 rounded-full items-center justify-center mr-2"
                      style={{ backgroundColor: sectionId === s.id ? "rgba(255,255,255,0.3)" : colors.surfaceSecondary }}
                    >
                      <Text className="text-xs font-bold" style={{ color: sectionId === s.id ? "#FFF" : colors.textSecondary }}>
                        {index + 1}
                      </Text>
                    </View>
                    <Text style={{ color: sectionId === s.id ? "#FFF" : colors.text }}>
                      {s.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          {/* Question Type */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Type
            </Text>
            <View className="flex-row">
              {QUESTION_TYPES.map((t) => (
                <TouchableOpacity
                  key={t.value}
                  onPress={() => setQuestionType(t.value)}
                  className="flex-1 rounded-xl p-3 mr-2 items-center"
                  style={{
                    backgroundColor: questionType === t.value ? `${colors.primary}20` : colors.surface,
                    borderWidth: 2,
                    borderColor: questionType === t.value ? colors.primary : colors.border,
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: questionType === t.value }}
                >
                  <Ionicons
                    name={t.icon as any}
                    size={20}
                    color={questionType === t.value ? colors.primary : colors.textSecondary}
                  />
                  <Text
                    className="text-xs mt-1"
                    style={{ color: questionType === t.value ? colors.primary : colors.textSecondary }}
                  >
                    {t.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Question Text */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Vraag *
            </Text>
            <TextInput
              value={question}
              onChangeText={setQuestion}
              placeholder="Typ je vraag..."
              placeholderTextColor={colors.textTertiary}
              multiline
              className="rounded-xl p-4"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                minHeight: 80,
                textAlignVertical: "top",
              }}
              accessibilityLabel="Vraag tekst"
            />
          </View>

          {/* Question Image */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Afbeelding (optioneel)
            </Text>
            
            {imageUrl ? (
              <View className="rounded-xl overflow-hidden" style={{ borderWidth: 1, borderColor: colors.border }}>
                <Image
                  source={{ uri: imageUrl }}
                  className="w-full h-40"
                  resizeMode="cover"
                />
                <TouchableOpacity
                  onPress={handleRemoveImage}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full items-center justify-center"
                  style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
                >
                  <Ionicons name="close" size={18} color="#FFFFFF" />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                onPress={handlePickImage}
                disabled={isUploadingImage}
                className="rounded-xl p-6 items-center justify-center"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 2,
                  borderColor: colors.border,
                  borderStyle: "dashed",
                }}
              >
                {isUploadingImage ? (
                  <ActivityIndicator color={colors.primary} />
                ) : (
                  <>
                    <Ionicons name="image-outline" size={32} color={colors.textTertiary} />
                    <Text className="mt-2 text-sm" style={{ color: colors.textSecondary }}>
                      Tik om afbeelding toe te voegen
                    </Text>
                    <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                      Wordt getoond op het grote scherm
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>

          {/* Category */}
          {categories.length > 0 && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Categorie
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <TouchableOpacity
                  onPress={() => setCategoryId(null)}
                  className="px-4 py-2 rounded-full mr-2"
                  style={{
                    backgroundColor: categoryId === null ? colors.primary : colors.surface,
                    borderWidth: categoryId === null ? 0 : 1,
                    borderColor: colors.border,
                  }}
                >
                  <Text style={{ color: categoryId === null ? "#FFF" : colors.text }}>Geen</Text>
                </TouchableOpacity>
                {categories.map((c) => (
                  <TouchableOpacity
                    key={c.id}
                    onPress={() => setCategoryId(c.id)}
                    className="px-4 py-2 rounded-full mr-2 flex-row items-center"
                    style={{ backgroundColor: categoryId === c.id ? c.color : colors.surface }}
                  >
                    <Ionicons
                      name={c.icon as any}
                      size={14}
                      color={categoryId === c.id ? "#FFF" : c.color}
                    />
                    <Text className="ml-1" style={{ color: categoryId === c.id ? "#FFF" : colors.text }}>
                      {c.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          {/* Difficulty */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Moeilijkheid
            </Text>
            <View className="flex-row">
              {DIFFICULTY_OPTIONS.map((d) => (
                <TouchableOpacity
                  key={d.value}
                  onPress={() => setDifficulty(d.value)}
                  className="flex-1 rounded-xl py-3 mr-2 items-center"
                  style={{ backgroundColor: difficulty === d.value ? d.color : `${d.color}20` }}
                >
                  <Text style={{ color: difficulty === d.value ? "#FFF" : d.color }}>{d.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Options - Multiple Choice */}
          {questionType === "multiple_choice" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Antwoordopties * (tik om correct antwoord te selecteren)
              </Text>
              {options.map((opt, i) => (
                <View key={i} className="flex-row items-center mb-2">
                  <TouchableOpacity
                    onPress={() => handleCorrectChange(i)}
                    className="w-10 h-10 rounded-full items-center justify-center mr-2"
                    style={{
                      backgroundColor: opt.is_correct ? "#10B98130" : colors.surface,
                      borderWidth: 2,
                      borderColor: opt.is_correct ? "#10B981" : colors.border,
                    }}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: opt.is_correct }}
                    accessibilityLabel={`Optie ${i + 1} als correct markeren`}
                  >
                    {opt.is_correct && <Ionicons name="checkmark" size={18} color="#10B981" />}
                  </TouchableOpacity>
                  <TextInput
                    value={opt.text}
                    onChangeText={(t) => handleOptionChange(i, t)}
                    placeholder={`Optie ${i + 1}`}
                    placeholderTextColor={colors.textTertiary}
                    className="flex-1 rounded-xl px-4 py-3"
                    style={{ backgroundColor: colors.surface, color: colors.text }}
                    accessibilityLabel={`Optie ${i + 1} tekst`}
                  />
                  {options.length > 2 && (
                    <TouchableOpacity
                      onPress={() => handleRemoveOption(i)}
                      className="w-10 h-10 rounded-full items-center justify-center ml-2"
                      style={{ backgroundColor: colors.error + "20" }}
                      accessibilityLabel={`Optie ${i + 1} verwijderen`}
                    >
                      <Ionicons name="remove" size={18} color={colors.error} />
                    </TouchableOpacity>
                  )}
                </View>
              ))}
              {options.length < 6 && (
                <TouchableOpacity
                  onPress={handleAddOption}
                  className="flex-row items-center justify-center py-3 rounded-xl"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                >
                  <Ionicons name="add" size={18} color={colors.primary} />
                  <Text className="ml-2" style={{ color: colors.primary }}>
                    Optie toevoegen
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* True/False */}
          {questionType === "true_false" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Correct antwoord
              </Text>
              <View className="flex-row">
                <TouchableOpacity
                  onPress={() => setTrueFalse(true)}
                  className="flex-1 rounded-xl py-4 mr-2 items-center"
                  style={{
                    backgroundColor: options[0]?.is_correct ? "#10B98130" : colors.surface,
                    borderWidth: 2,
                    borderColor: options[0]?.is_correct ? "#10B981" : colors.border,
                  }}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: options[0]?.is_correct }}
                >
                  <Ionicons name="checkmark-circle" size={28} color={options[0]?.is_correct ? "#10B981" : colors.textSecondary} />
                  <Text className="mt-2" style={{ color: options[0]?.is_correct ? "#10B981" : colors.text }}>
                    Waar
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setTrueFalse(false)}
                  className="flex-1 rounded-xl py-4 ml-2 items-center"
                  style={{
                    backgroundColor: options[1]?.is_correct ? "#EF444430" : colors.surface,
                    borderWidth: 2,
                    borderColor: options[1]?.is_correct ? "#EF4444" : colors.border,
                  }}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: options[1]?.is_correct }}
                >
                  <Ionicons name="close-circle" size={28} color={options[1]?.is_correct ? "#EF4444" : colors.textSecondary} />
                  <Text className="mt-2" style={{ color: options[1]?.is_correct ? "#EF4444" : colors.text }}>
                    Onwaar
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Explanation */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Uitleg (optioneel)
            </Text>
            <TextInput
              value={explanation}
              onChangeText={setExplanation}
              placeholder="Extra uitleg die getoond wordt na beantwoorden"
              placeholderTextColor={colors.textTertiary}
              multiline
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface, color: colors.text, minHeight: 60, textAlignVertical: "top" }}
              accessibilityLabel="Uitleg tekst"
            />
          </View>

          <View className="h-24" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
